import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimesCircle, faEnvelope, faRefresh, faArrowLeft } from '@/fontawesome';
import { Link } from 'react-router-dom';

const ALLOWED_CODE_CHARS = /[A-HJ-KM-NP-Z0-9]/;

export function filterCodeInput(value: string): string {
  return value
    .toUpperCase()
    .split('')
    .filter(ch => ALLOWED_CODE_CHARS.test(ch))
    .join('')
    .slice(0, 8);
}

interface StepVerificationCodeProps {
  email: string;
  registerSecret: string;
  devEmailCode: string | null;
  error: string;
  loading: boolean;
  resendCooldown: boolean;
  showLoginLink: boolean;
  onRegisterSecretChange: (value: string) => void;
  onSubmit: () => void;
  onResendCode: () => void;
  onBack: () => void;
}

const INPUT_CLASS = "w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors";

export default function StepVerificationCode({
  email,
  registerSecret,
  devEmailCode,
  error,
  loading,
  resendCooldown,
  showLoginLink,
  onRegisterSecretChange,
  onSubmit,
  onResendCode,
  onBack,
}: StepVerificationCodeProps) {
  const isCodeComplete = registerSecret.length === 8;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isCodeComplete && !loading) {
      onSubmit();
    }
  };

  return (
    <form className="space-y-4" onSubmit={handleSubmit}>
      {/* Info banner */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0 mt-0.5">
            <FontAwesomeIcon icon={faEnvelope} className="h-5 w-5 text-blue-500 dark:text-blue-400" />
          </div>
          <div className="ml-3">
            <p className="text-blue-800 dark:text-blue-300 text-sm">
              Wir haben einen Bestätigungscode an <span className="font-semibold">{email}</span> gesendet. Bitte gib den Code unten ein.
            </p>
          </div>
        </div>
      </div>

      {/* Code input */}
      <div>
        <label htmlFor="registerSecret" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Bestätigungscode <span className="text-red-500">*</span>
        </label>
        <input
          id="registerSecret"
          type="text"
          required
          maxLength={8}
          value={registerSecret}
          onChange={(e) => onRegisterSecretChange(filterCodeInput(e.target.value))}
          className={`${INPUT_CLASS} font-mono text-lg tracking-widest uppercase text-center`}
          placeholder="XXXXXXXX"
          autoComplete="one-time-code"
          autoFocus
        />
        {/* Dev/local helper: backend returns the code so testers can register with fake emails (never populated in production) */}
        {devEmailCode !== null && (
          <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
            <p className="text-amber-800 dark:text-amber-300 text-sm">
              Email-Code: <span className="font-mono font-semibold tracking-widest">{devEmailCode}</span>
            </p>
          </div>
        )}
      </div>

      {/* Error display */}
      {error !== '' && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <FontAwesomeIcon icon={faTimesCircle} className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-red-700 dark:text-red-400 text-sm font-medium">{error}</p>
              {showLoginLink && (
                <Link
                  to="/login"
                  className="text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 text-sm font-medium underline mt-1 inline-block"
                >
                  Jetzt anmelden
                </Link>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Submit button */}
      <button
        type="submit"
        disabled={!isCodeComplete || loading}
        className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer transition-colors"
      >
        {loading ? (
          <>
            <FontAwesomeIcon icon={faSpinner} className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" />
            Registrierung läuft...
          </>
        ) : (
          'Registrieren'
        )}
      </button>

      <p className="text-xs text-center text-gray-500 dark:text-gray-400">
        Deine Daten bleiben selbstverständlich geschützt.
      </p>

      {/* Navigation links */}
      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium cursor-pointer"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="w-3 h-3 mr-1.5" />
          Zurück
        </button>
        <button
          type="button"
          onClick={onResendCode}
          disabled={resendCooldown}
          className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        >
          <FontAwesomeIcon icon={faRefresh} className="w-3 h-3 mr-1.5" />
          {resendCooldown ? 'Bitte warten...' : 'Code erneut senden'}
        </button>
      </div>
    </form>
  );
}
